import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { useTypedSelector } from '@Features/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, MenuItem } from '@mui/material';
import Permissions from '@Pages/Assistants/Components/AssistantPermissions/Permissions';
import { GenderTypes } from '@Pages/Assistants/Constants/AssistantsForm.constants';
import { ASSISTANT_SCHEMA } from '@Pages/Assistants/Schema/Assistant.Schema';
import { AssistantsRequester } from '@Pages/Assistants/Services/Assistants.Requester';
import { Assistants } from '@Pages/Assistants/Types/Assistants.types';
import { useSnackbar } from '@Providers/useSnackbar';
import { Divider, FlexLayout, GridLayout } from '@Styled/utilities';
import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

const EditAssistantInfo = () => {
	const { displaySnackbar } = useSnackbar();
	const { currentEntity } = useTypedSelector((state) => state.Assistants);
	const defaultValues = useMemo<Assistants>(() => {
		if (currentEntity) {
			const defaults: Assistants = {
				...currentEntity,
				first_name: currentEntity?.first_name,
				last_name: currentEntity?.last_name,
				email: currentEntity?.email,
				phone_number: currentEntity?.phone_number,
				gender: currentEntity?.gender,
				password: currentEntity?.password,
				instructor: {
					label: currentEntity?.instructor?.label,
					is_active: currentEntity?.instructor?.is_active,
					grants: currentEntity?.instructor.grants?.map((_) => _.id) as any[],
				},
			};
			return defaults;
		} else {
			return {} as Assistants;
		}
	}, []);

	const { getValues, control, formState, watch, ...formMethods } =
		useForm<Assistants>({
			resolver: yupResolver(ASSISTANT_SCHEMA),
			mode: 'all',
			defaultValues,
		});

	const onSave = async (form: Assistants) => {
		try {
			const {
				data: { data },
			} = await AssistantsRequester.getInstance().update(form);
			displaySnackbar(
				'success',
				`Assistant ${data.first_name} updated successfully`
			);
		} catch (e) {
			const msg = e?.response?.data?.message || 'Something went wrong';
			displaySnackbar('error', msg);
		}
	};

	return (
		<FormProvider
			control={control}
			getValues={getValues}
			formState={formState}
			watch={watch}
			{...formMethods}
		>
			<form>
				<GridLayout
					gridGap={'2rem'}
					gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
					marginRight="5.5rem"
					width="75%"
					padding="2.438rem 1.25rem"
				>
					<EdFormControl
						control={control}
						name="first_name"
						label="First Name"
						required
					/>
					<EdFormControl
						control={control}
						name="last_name"
						label="Last Name"
						required
					/>
					<EdFormControl
						control={control}
						required
						name="email"
						label="Email"
					/>
					<EdFormControl
						control={control}
						required
						name="phone_number"
						label="Phone Number"
					/>
					<EdFormControl
						control={control}
						required
						name="password"
						label="Password"
					/>
					<EdFormControl control={control} select name="gender" label="Gender">
						{GenderTypes.map((_, index) => (
							<MenuItem key={`${index}-${_}`} value={_}>{`${_}`}</MenuItem>
						))}
					</EdFormControl>
					<EdFormControl
						control={control}
						required
						name="instructor.label"
						label="Label"
					/>
					<EdFormControl
						control={control}
						switch
						name="instructor.is_active"
						label="Active"
						defaultChecked={true}
					/>
				</GridLayout>
				<FlexLayout width="75%" flexDirection="column">
					<Divider label="Permissions" />
					<Permissions name="instructor.grants" />
				</FlexLayout>
				<FlexLayout
					justifyContent="flex-end"
					gridGap="1rem"
					width="75%"
					marginTop="2.938rem"
				>
					<Button
						variant="contained"
						color="warning"
						disabled={!formState.isDirty}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						disabled={!formState.isDirty}
						onClick={() => {
							const data = getValues();
							onSave(data);
						}}
					>
						Save
					</Button>
				</FlexLayout>
			</form>
		</FormProvider>
	);
};

export default EditAssistantInfo;
